import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import {
  analyticsApi,
  ApiRequestError,
  type RadarParentOption,
  type RadarPointDto,
} from "../../api/api";
import { useAuth } from "../../context/AuthContext";
import {
  AnalyticsFilterBar,
  type AnalyticsFiltersState,
} from "../../components/ui/AnalyticsFilterBar";
import { PageHeader } from "../../components/ui/PageHeader";

type Mode = "functional" | "transversal";

function grainLabel(grain: number | string | undefined, t: (k: string) => string): string {
  const g = typeof grain === "number" ? grain : grain === "business" ? 1 : grain === "sub" ? 2 : 2;
  if (g <= 0) return t("radar_grain_macro");
  if (g === 1) return t("radar_axes_business");
  if (g === 2) return t("sub_processes");
  return t("radar_grain_task");
}

export function RadarPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isExpert = (user?.accessLevel ?? 0) < 50;
  const [filters, setFilters] = useState<AnalyticsFiltersState>(() => ({
    year: new Date().getFullYear(),
    userId: isExpert && user ? user.id : undefined,
  }));
  const [mode, setMode] = useState<Mode>("functional");
  /** Transversal drill stack of parentPath keys. */
  const [pathStack, setPathStack] = useState<string[]>([]);
  const [data, setData] = useState<RadarPointDto[]>([]);
  const [parents, setParents] = useState<RadarParentOption[]>([]);
  const [grain, setGrain] = useState<number | string>(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const parentPath = pathStack.length ? pathStack[pathStack.length - 1]! : null;

  useEffect(() => {
    if (isExpert && user) {
      setFilters((f) => ({ ...f, userId: user.id }));
    }
  }, [isExpert, user]);

  useEffect(() => {
    setPathStack([]);
  }, [mode, filters.year, filters.domain, filters.applicationId, filters.userId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const q = isExpert && user ? { ...filters, userId: user.id } : filters;
    void analyticsApi
      .radar({
        ...q,
        mode,
        parentPath: mode === "transversal" && parentPath ? parentPath : undefined,
        grain: mode === "transversal" && !parentPath ? 1 : undefined,
      })
      .then((res) => {
        if (!cancelled) {
          setData(res.points);
          setParents(res.parents ?? []);
          setGrain(res.grain);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setData([]);
          setParents([]);
          setError(err instanceof ApiRequestError ? err.message : t("analytics_load_failed"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, mode, parentPath, isExpert, user, t]);

  const avgScore =
    data.length > 0
      ? (data.reduce((s, d) => s + d.avg, 0) / data.length).toFixed(1)
      : null;
  const stroke = mode === "transversal" ? "#7C3AED" : "#2E74B5";

  return (
    <div className="sm-page">
      <PageHeader title={t("radar_chart")} />
      <AnalyticsFilterBar filters={filters} onChange={setFilters} showUser={!isExpert} />
      <div className="sm-pill-row" style={{ marginBottom: 12 }}>
        <button
          type="button"
          className={`sm-btn ${mode === "functional" ? "sm-btn-primary" : "sm-btn-secondary"} sm-btn-sm`}
          onClick={() => setMode("functional")}
        >
          {t("functional_skills")}
        </button>
        <button
          type="button"
          className={`sm-btn ${mode === "transversal" ? "sm-btn-primary" : "sm-btn-secondary"} sm-btn-sm`}
          onClick={() => setMode("transversal")}
        >
          {t("transversal_skills")}
        </button>
      </div>

      {mode === "transversal" ? (
        <div className="sm-card sm-card-pad sm-analytics-filters" style={{ marginBottom: 12 }}>
          <label className="sm-label">
            {t("radar_zoom")}
            <select
              className="sm-select"
              value={parentPath ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) setPathStack([]);
                else setPathStack([v]);
              }}
            >
              <option value="">{t("radar_zoom_aggregated")}</option>
              {parents.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.macroProcess} · {p.label} ({p.childCount ?? p.subCount})
                </option>
              ))}
            </select>
          </label>
          {pathStack.length > 0 ? (
            <div className="sm-pill-row" style={{ alignSelf: "flex-end" }}>
              <button
                type="button"
                className="sm-btn sm-btn-secondary sm-btn-sm"
                onClick={() => setPathStack((s) => s.slice(0, -1))}
              >
                {t("radar_zoom_up")}
              </button>
              <button
                type="button"
                className="sm-btn sm-btn-secondary sm-btn-sm"
                onClick={() => setPathStack([])}
              >
                {t("radar_zoom_back")}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {error ? <p className="sm-error">{error}</p> : null}
      {loading ? (
        <p className="sm-muted">{t("loading")}</p>
      ) : data.length === 0 ? (
        <div className="sm-card sm-card-pad sm-muted">{t("no_data")}</div>
      ) : (
        <div className="sm-card sm-card-pad">
          <div className="sm-analytics-meta">
            <span className="sm-muted">
              {data.length} {grainLabel(grain, t)}
              {parentPath ? ` · ${parentPath.split("||").slice(-1)[0]}` : ""}
            </span>
            {avgScore ? (
              <span style={{ fontWeight: 600, color: stroke }}>
                {t("avg_score")}: {avgScore} / 4
              </span>
            ) : null}
          </div>
          <ResponsiveContainer width="100%" height={420}>
            <RadarChart data={data} margin={{ top: 20, right: 40, bottom: 20, left: 40 }}>
              <PolarGrid stroke="#E5E7EB" />
              <PolarAngleAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: "#6B7280" }}
                tickFormatter={(v: string) => (v.length > 28 ? `${v.slice(0, 26)}…` : v)}
              />
              <PolarRadiusAxis angle={90} domain={[0, 4]} tickCount={5} tick={{ fontSize: 10 }} />
              <Radar
                name={mode === "functional" ? t("functional_skills") : t("transversal_skills")}
                dataKey="avg"
                stroke={stroke}
                fill={stroke}
                fillOpacity={0.25}
              />
              <Legend />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>

          {mode === "transversal" && parents.length > 0 ? (
            <div className="sm-radar-drill" style={{ marginTop: 12 }}>
              <p className="sm-muted" style={{ marginBottom: 8 }}>
                {t("radar_drill_hint")}
              </p>
              <div className="sm-pill-row">
                {parents.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    className="sm-btn sm-btn-secondary sm-btn-sm"
                    onClick={() => setPathStack((s) => [...s, p.key])}
                  >
                    {p.label} ↓
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
